import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, loginUser, registerUser, logoutUser, setToken, getToken } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState(false); // transient backend/network failure during bootstrap

  const boot = useCallback(async () => {
    setBootError(false);
    if (!getToken()) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      setUser(await fetchMe());
      setReady(true);
    } catch (err) {
      // Only treat a genuine 401 as "not signed in" and clear the session.
      // Transient failures (no response = network/CORS/Cloudflare, or a 5xx while the
      // backend is redeploying) must NOT wipe the user's token — otherwise a brief
      // deploy blip silently logs everyone out.
      if (err?.response?.status === 401) {
        setToken(null);
        setUser(false);
      } else {
        setBootError(true);
      }
      setReady(true);
    }
  }, []);

  useEffect(() => {
    boot();
  }, [boot]);

  const login = useCallback(async (email, password) => {
    const data = await loginUser(email, password);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await registerUser(payload);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const adoptSession = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await logoutUser(); } catch (_) {}
    setToken(null);
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    try { setUser(await fetchMe()); } catch (_) {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, bootError, retry: boot, login, register, adoptSession, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
